'use client'

import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { createClientLogger } from '@/lib/logger/client'

const log = createClientLogger('NavDrawer')
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import {
  MenuIcon,
  X,
  User,
  Users,
  UserPlus,
  UserMinus,
  UserCheck,
  UserX,
  FileText,
  Film,
  Bell,
  BellOff,
  Settings,
  Plus,
  PlusCircle,
  Minus,
  MinusCircle,
  ScanLine,
  BarChart,
  BarChart2,
  BarChart3,
  Shield,
  ShieldCheck,
  Home,
  LogOut,
  ShoppingCart,
  ShoppingBag,
  LucideIcon,
  Heart,
  HeartHandshake,
  HeartPulse,
  HeartCrack,
  HeartOff,
  HandHeart,
  Handshake,
  Star,
  Laugh,
  PartyPopper,
  Mail,
  Phone,
  Users2,
  UserCog,
  UserCircle,
  UserSquare,
  Wallet,
  Tags,
  BadgeCheck,
  BadgeDollarSign,
  Circle,
  Square,
  Triangle,
  Diamond,
  Hexagon,
  Octagon,
  Waves,
  Rainbow,
  Sparkle,
  Palette,
  Paintbrush,
  MapPin,
  Map,
  Navigation,
  Compass,
  Globe,
  Calendar,
  Clock,
  Timer,
  AlarmClock,
  Search,
  Info,
  HelpCircle,
  Book,
  BookOpen,
  Bookmark,
  BookMarked,
  Library,
  GraduationCap,
  Trophy,
  Target,
  Zap,
  Sparkles,
  Flame,
  Gift,
  Ticket,
  Camera,
  Video,
  Music,
  Image,
  Folder,
  FolderOpen,
  File,
  Files,
  FilePlus,
  Download,
  Upload,
  Link as LinkIcon,
  Link2,
  ExternalLink,
  Paperclip,
  MessageCircle,
  MessageSquare,
  Send,
  Share,
  Share2,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Key,
  Award,
  Briefcase,
  DollarSign,
  Euro,
  CreditCard,
  Building,
  Building2,
  Store,
  Warehouse,
  Package,
  Flag,
  Tag,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUp,
  ChevronsDown,
  MoreHorizontal,
  MoreVertical,
  Check,
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  XCircle,
  Edit,
  Edit2,
  Edit3,
  Pencil,
  Trash,
  Trash2,
  Copy,
  Scissors,
  Sliders,
  Filter,
  Maximize,
  Minimize,
  Play,
  Pause,
  Volume2,
  LineChart,
  PieChart,
  TrendingUp,
  TrendingDown,
  Layout,
  Grid,
  Columns,
  Rows,
  List,
  Layers,
  Sun,
  Moon,
  Cloud,
  CloudRain,
  Leaf,
  TreePine,
  Coffee,
  Pizza,
  Utensils,
  Smile,
  Frown,
  Meh,
  ThumbsUp,
  ThumbsDown,
  Loader,
  RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/Button'
import { useUserContext } from '@/providers/UserProvider'
import { useNotificationCount } from '@/hooks/useNotificationCount'
import { cn } from '@/utilities/cn'
import type { FeatureFlag } from '@/utilities/getFeatureFlags'
import type { Header } from '@/payload-types'
import { CMSLink } from '@/components/Link'
import { getLinkUrl } from '@/fields/link'

// Icon mapping for CMS links
const iconMap: Record<string, LucideIcon> = {
  Home,
  Menu: MenuIcon,
  X,
  // Navigation
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUp,
  ChevronsDown,
  MoreHorizontal,
  MoreVertical,
  // Users
  User,
  Users,
  Users2,
  UserPlus,
  UserMinus,
  UserCheck,
  UserX,
  UserCog,
  UserCircle,
  UserSquare,
  // Communication
  Mail,
  MessageCircle,
  MessageSquare,
  Send,
  Phone,
  Bell,
  BellOff,
  // Files
  File,
  FileText,
  Files,
  Folder,
  FolderOpen,
  FilePlus,
  Download,
  Upload,
  // Media
  Image,
  Film,
  Video,
  Camera,
  Music,
  Play,
  Pause,
  Volume2,
  // Commerce
  ShoppingCart,
  ShoppingBag,
  CreditCard,
  Wallet,
  DollarSign,
  Euro,
  Tag,
  Tags,
  Gift,
  Ticket,
  BadgeCheck,
  BadgeDollarSign,
  // Location
  MapPin,
  Map,
  Navigation,
  Compass,
  Globe,
  // Time
  Calendar,
  Clock,
  Timer,
  AlarmClock,
  // Status
  Check,
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
  XCircle,
  HelpCircle,
  // Social
  Share,
  Share2,
  Heart,
  HeartHandshake,
  HeartPulse,
  HeartCrack,
  HeartOff,
  HandHeart,
  Handshake,
  Star,
  ThumbsUp,
  ThumbsDown,
  Laugh,
  PartyPopper,
  // Security
  Lock,
  Unlock,
  Key,
  Shield,
  ShieldCheck,
  Eye,
  EyeOff,
  // Links
  Link: LinkIcon,
  Link2,
  ExternalLink,
  Paperclip,
  // Edit
  Edit,
  Edit2,
  Edit3,
  Pencil,
  Trash,
  Trash2,
  Copy,
  Scissors,
  // UI Controls
  Settings,
  Sliders,
  Filter,
  Search,
  Maximize,
  Minimize,
  Plus,
  PlusCircle,
  Minus,
  MinusCircle,
  // Buildings
  Building,
  Building2,
  Store,
  Warehouse,
  // Objects
  Book,
  BookOpen,
  Bookmark,
  BookMarked,
  Library,
  Award,
  Trophy,
  Target,
  Flag,
  Briefcase,
  Package,
  // Education
  GraduationCap,
  // Actions
  Zap,
  Sparkles,
  Flame,
  Loader,
  RefreshCw,
  // Charts
  BarChart,
  BarChart2,
  BarChart3,
  LineChart,
  PieChart,
  TrendingUp,
  TrendingDown,
  // Layout
  Layout,
  Grid,
  Columns,
  Rows,
  List,
  Layers,
  // Weather
  Sun,
  Moon,
  Cloud,
  CloudRain,
  Leaf,
  TreePine,
  // Shapes
  Circle,
  Square,
  Triangle,
  Diamond,
  Hexagon,
  Octagon,
  // Misc
  Waves,
  Rainbow,
  Sparkle,
  Coffee,
  Pizza,
  Utensils,
  Smile,
  Frown,
  Meh,
  Palette,
  Paintbrush,
}

interface AppNavItem {
  label: string
  href: string
  icon: LucideIcon
  // Any-of role gate. Undefined = visible to all logged-in users.
  roles?: string[]
  flag?: string
  hidden?: boolean
}

interface NavSection {
  id: string
  label?: string
  items: AppNavItem[]
}

// Single flat menu, deduplicated across roles, grouped into sections.
// Each item lists every role that grants access — no more per-role tab duplication.
const navSections: NavSection[] = [
  {
    id: 'account',
    items: [
      { label: 'Mijn Profiel', href: '/mijn/profiel', icon: User, flag: 'nav-profile' },
      { label: 'Instellingen', href: '/mijn/instellingen', icon: UserCog },
      { label: 'Dashboard', href: '/app', icon: Home, flag: 'nav-dashboard' },
      { label: 'Notificaties', href: '/mijn/notificaties', icon: Bell, flag: 'nav-notifications' },
    ],
  },
  {
    id: 'work',
    label: 'Mijn werk',
    items: [
      {
        label: 'Scripts',
        href: '/scripts',
        icon: FileText,
        flag: 'nav-scripts',
        roles: ['Student', 'Coach', 'Teacher', 'School'],
      },
      {
        label: 'Mijn groepen',
        href: '/mijn/groepen',
        icon: Users,
        flag: 'nav-groups',
        roles: ['Student', 'Coach', 'Teacher', 'School'],
      },
      {
        label: 'Mijn accommodaties',
        href: '/mijn/accommodaties',
        icon: MapPin,
        flag: 'nav-accommodations',
        roles: ['Parent', 'School'],
      },
      {
        label: 'Mijn stichtingen & Fondsen',
        href: '/mijn/stichtingen-en-fondsen',
        icon: Building2,
        flag: 'nav-foundations',
        roles: ['Foundation'],
      },
      {
        label: 'Projecten',
        href: '/projecten',
        icon: Film,
        flag: 'nav-projects',
        roles: ['Student', 'Coach', 'School'],
        hidden: true,
      },
      { label: 'Nieuw Project', href: '/nieuw-project', icon: Plus, roles: ['Coach'] },
      { label: 'Ticket Scanner', href: '/mijn/scanner', icon: ScanLine, roles: ['Coach'] },
    ],
  },
  {
    id: 'admin',
    label: 'Beheer',
    items: [
      { label: 'Beheer Dashboard', href: '/beheer', icon: Home, roles: ['Admin'] },
      { label: 'Moderatie', href: '/beheer/moderatie', icon: Shield, roles: ['Moderator', 'Admin'] },
      { label: 'Bestellingen', href: '/beheer/bestellingen', icon: ShoppingCart, roles: ['Admin'] },
      { label: 'Ticket Preview', href: '/beheer/ticket-preview', icon: Ticket, roles: ['Admin'] },
      { label: 'Statistieken', href: '/beheer/statistieken', icon: BarChart3, roles: ['Admin'] },
      { label: 'CMS Admin', href: '/admin', icon: Settings, roles: ['Admin'] },
    ],
  },
]

// Rendered as its own section after CMS "Verkennen" items.
const accountFooterItems: AppNavItem[] = [
  { label: 'Producten', href: '/producten', icon: ShoppingBag, flag: 'nav-products' },
]

interface NavDrawerProps {
  featureFlags: FeatureFlag[]
  header?: Header
  context?: 'app' | 'frontend' // Kept for backward compatibility but no longer affects menu display
}

export function NavDrawer({ featureFlags, header }: NavDrawerProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const { user, signOut } = useUserContext()
  const { unreadCount } = useNotificationCount()
  const pathname = usePathname()
  const router = useRouter()

  // Track mounted state for portal (SSR compatibility)
  useEffect(() => {
    setIsMounted(true)
  }, [])

  // Close drawer on route change
  useEffect(() => {
    setIsDrawerOpen(false)
  }, [pathname])

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isDrawerOpen])

  const handleSignOut = async () => {
    try {
      await signOut()
      router.push('/')
    } catch (error) {
      log.error({ error }, 'Sign out error')
    }
  }

  const userRoles = user?.roles ?? []

  const isItemVisible = (item: AppNavItem): boolean => {
    if (item.hidden) return false
    if (item.flag) {
      const flag = featureFlags.find((f) => f.name === item.flag)
      if (flag && !flag.enabled) return false
    }
    if (item.roles && item.roles.length > 0) {
      return item.roles.some((role) => (userRoles as string[]).includes(role))
    }
    return true
  }

  const visibleSections = navSections
    .map((section) => ({ ...section, items: section.items.filter(isItemVisible) }))
    .filter((section) => section.items.length > 0)

  const visibleFooterItems = accountFooterItems.filter(isItemVisible)

  // Get CMS navigation items, filtered by feature flags
  const cmsNavItems = (header?.drawerItems || []).filter((item) => {
    if (item.featureFlag) {
      const flagValue = featureFlags.find((f) => f.name === item.featureFlag)
      if (flagValue && !flagValue.enabled) {
        return false
      }
    }
    return true
  })

  const isActive = (href: string) => {
    if (href === '/app') {
      return pathname === href || pathname.startsWith('/app/')
    }
    if (href === '/scripts') {
      return pathname === href || pathname.startsWith('/scripts/') || pathname.startsWith('/mijn/scripts/')
    }
    if (href === '/mijn/groepen') {
      return pathname === href || pathname.startsWith('/mijn/groepen/')
    }
    if (href === '/mijn/stichtingen-en-fondsen') {
      return pathname === href || pathname.startsWith('/mijn/stichtingen-en-fondsen/')
    }
    if (href === '/mijn/accommodaties') {
      return pathname === href || pathname.startsWith('/mijn/accommodaties/')
    }
    if (href === '/mijn/instellingen') {
      return pathname === href || pathname.startsWith('/mijn/instellingen/')
    }
    if (href === '/beheer/bestellingen') {
      return pathname === href || pathname.startsWith('/beheer/bestellingen/')
    }
    if (href === '/beheer/moderatie') {
      return pathname === href || pathname.startsWith('/beheer/moderatie/')
    }
    return pathname === href
  }

  // Portal content for backdrop and drawer
  const drawerPortalContent = isMounted ? (
    <>
      {/* Backdrop */}
      {isDrawerOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[60]"
          onClick={() => setIsDrawerOpen(false)}
        />
      )}

      {/* Navigation Drawer */}
      <div
        className={cn(
          'fixed top-0 right-0 h-full w-80 bg-white shadow-xl z-[60] transform transition-transform duration-300 ease-in-out overflow-y-auto flex flex-col',
          'lg:w-96',
          isDrawerOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            {user ? (
              <div className={'mt-1'}>
                <h2 className="font-semibold text-slate-900">Welkom, {user.name}</h2>
              </div>
            ) : (
              <h2 className="font-semibold text-slate-900">Menu</h2>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsDrawerOpen(false)}
            className="p-2"
          >
            <X className="w-5 h-5" />
            <span className="sr-only">Menu sluiten</span>
          </Button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 p-4 overflow-y-auto">
          {user ? (
            <div className="space-y-6">
              {visibleSections.map((section) => (
                <div key={section.id} className="space-y-1">
                  {section.label && (
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 px-3 pb-1">
                      {section.label}
                    </h3>
                  )}
                  {section.items.map((item) => {
                    const Icon = item.icon
                    const active = isActive(item.href)
                    const showBadge = item.href === '/mijn/notificaties' && unreadCount > 0

                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                          active
                            ? 'bg-primary/10 text-primary border border-primary/20'
                            : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900',
                        )}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        <span className="flex-1">{item.label}</span>
                        {showBadge && (
                          <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[1.25rem] text-center">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        )}
                      </Link>
                    )
                  })}
                </div>
              ))}

              {(cmsNavItems.length > 0 || visibleFooterItems.length > 0) && (
                <div className="space-y-1">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 px-3 pb-1">
                    Verkennen
                  </h3>
                  {cmsNavItems.map(({ link }, i) => {
                    const href = getLinkUrl(link)
                    const active = isActive(href)
                    const iconName = link.icon
                    const Icon = iconName ? iconMap[iconName] : null

                    return (
                      <Link
                        key={`cms-${i}`}
                        href={href}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                          active
                            ? 'bg-primary/10 text-primary border border-primary/20'
                            : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900',
                        )}
                      >
                        {Icon && <Icon className="w-5 h-5 flex-shrink-0" />}
                        <span className="flex-1">{link.label}</span>
                      </Link>
                    )
                  })}
                  {visibleFooterItems.map((item) => {
                    const Icon = item.icon
                    const active = isActive(item.href)
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                          active
                            ? 'bg-primary/10 text-primary border border-primary/20'
                            : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900',
                        )}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        <span className="flex-1">{item.label}</span>
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* CMS Navigation for non-logged in users */}
              {cmsNavItems.length > 0 && (
                <div className="space-y-2 mb-4">
                  {cmsNavItems.map(({ link }, i) => {
                    const href = getLinkUrl(link)
                    const active = isActive(href)
                    const iconName = link.icon
                    const Icon = iconName ? iconMap[iconName] : null

                    return (
                      <Link
                        key={i}
                        href={href}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                          active
                            ? 'bg-primary/10 text-primary border border-primary/20'
                            : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900',
                        )}
                      >
                        {Icon && <Icon className="w-5 h-5 flex-shrink-0" />}
                        <span className="flex-1">{link.label}</span>
                      </Link>
                    )
                  })}
                </div>
              )}

              <p className="text-slate-600 text-sm">
                Log in om toegang te krijgen tot alle functies.
              </p>
              <div className="flex flex-col gap-2">
                <Link href="/inloggen" className="btn-primary text-center">
                  Inloggen
                </Link>
                <Link href="/registreren" className="btn-secondary text-center">
                  Registreren
                </Link>
              </div>
            </div>
          )}
        </nav>

        {/* Drawer Footer */}
        {user && (
          <div className="border-t border-slate-200 p-4 sticky bottom-0 bg-white">
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                onClick={handleSignOut}
                className="w-full justify-center text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Uitloggen
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  ) : null

  return (
    <>
      {/* Menu button */}
      <div className="relative">
        <MenuIcon
          className="size-8 text-primary cursor-pointer"
          onClick={() => setIsDrawerOpen(true)}
        />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </div>
      <span className="sr-only">Menu openen</span>

      {/* Render backdrop and drawer via portal to body */}
      {isMounted && createPortal(drawerPortalContent, document.body)}
    </>
  )
}
